import { motion, useAnimationControls } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const testimonials = [
  {
    quote: "This platform gave me the language to understand what really happened.",
    detail: "I spent twelve years believing my questions meant I lacked faith. Soul Safe Sanctuary finally gave me clarity and peace.",
    initials: "A.M.",
    context: "Survivor, 12 years in a high-control church",
  },
  {
    quote: "Soul Safe Counsel changed everything for me.",
    detail: "For the first time, I processed what happened without fear of being called a gossip. 100% confidential and deeply healing.",
    initials: "S.T.",
    context: "Survivor, healing journey in progress",
  },
  {
    quote: "Every church leader should do this training.",
    detail: "As a pastor, this opened my eyes to patterns I didn't know existed in my own leadership culture. It transformed how I lead.",
    initials: "D.W.",
    context: "Senior Pastor, 15 years in ministry",
  },
  {
    quote: "That is what healing feels like.",
    detail: "I cried through most of the recovery course. Not from pain, but from finally being understood. This is real transformation.",
    initials: "M.C.",
    context: "Soul Safe Sanctuary™ Graduate",
  },
  {
    quote: "It saved our faith and gave us courage.",
    detail: "My wife and I went through this together. It saved our faith and gave us the courage to find a healthy church.",
    initials: "T.B.",
    context: "Survivor couple",
  },
  {
    quote: "I was never the broken one — the system was.",
    detail: "I thought my discernment was rebellion. Soul Safe helped me realize the system was broken, not me.",
    initials: "R.K.",
    context: "Former ministry leader",
  },
];

export function TestimonialsSection() {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    let animationId: number;
    let scrollPos = container.scrollLeft || 0;
    const speed = 0.6;

    const scroll = () => {
      if (!isPaused && container) {
        scrollPos += speed;
        if (scrollPos >= container.scrollWidth / 2) {
          scrollPos = 0;
        }
        container.scrollLeft = scrollPos;
      }
      animationId = requestAnimationFrame(scroll);
    };

    animationId = requestAnimationFrame(scroll);
    return () => cancelAnimationFrame(animationId);
  }, [isPaused]);

  return (
    <section className="py-28 md:py-36 bg-foreground overflow-hidden">
      <div className="container px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-2xl mx-auto text-center mb-16"
        >
          <motion.p
            initial={{ opacity: 0, letterSpacing: "0.1em" }}
            whileInView={{ opacity: 1, letterSpacing: "0.25em" }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-xs font-semibold uppercase text-accent mb-6"
          >
            Testimonials
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-serif text-3xl md:text-5xl lg:text-6xl font-black text-primary-foreground mb-4 leading-tight tracking-tight"
          >
            Real People. Real Healing.
          </motion.h2>
        </motion.div>
      </div>

      {/* Scrolling carousel */}
      <div
        ref={scrollRef}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={() => setIsPaused(true)}
        onTouchEnd={() => setIsPaused(false)}
        className="flex gap-8 overflow-hidden px-6 cursor-default"
        style={{ scrollBehavior: "auto" }}
      >
        {[...testimonials, ...testimonials].map((item, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: (i % 6) * 0.08 }}
            whileHover={{ scale: 1.03, y: -6 }}
            className="flex-shrink-0 w-[380px] md:w-[480px] bg-primary-foreground rounded-2xl p-8 md:p-10 flex flex-col relative overflow-hidden group transition-shadow duration-500 hover:shadow-[0_25px_60px_-15px_rgba(42,157,143,0.25)]"
          >
            {/* Accent top bar */}
            <motion.div
              className="absolute top-0 left-0 right-0 h-1 bg-accent"
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: (i % 6) * 0.1 }}
              style={{ transformOrigin: "left" }}
            />

            {/* Quote icon */}
            <motion.div
              initial={{ opacity: 0, rotate: -20 }}
              whileInView={{ opacity: 0.1, rotate: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="absolute top-6 right-6"
            >
              <Quote className="w-16 h-16 text-accent" />
            </motion.div>

            {/* Stars */}
            <div className="flex gap-1 mb-6">
              {[...Array(5)].map((_, s) => (
                <motion.div
                  key={s}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: 0.3 + s * 0.08 }}
                >
                  <Star className="w-5 h-5 fill-accent text-accent" />
                </motion.div>
              ))}
            </div>

            {/* Bold quote */}
            <h3 className="font-serif text-xl md:text-2xl font-bold text-foreground mb-4 leading-snug relative z-10">
              "{item.quote}"
            </h3>

            {/* Detail */}
            <p className="text-base text-muted-foreground leading-relaxed mb-8 flex-1">
              {item.detail}
            </p>

            {/* Attribution */}
            <footer className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent/15 flex items-center justify-center">
                <span className="text-sm font-bold text-accent">{item.initials}</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">{item.initials}</p>
                <p className="text-xs text-muted-foreground">{item.context}</p>
              </div>
            </footer>
          </motion.div>
        ))}
      </div>

      {/* CTA */}
      <div className="container px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="text-center mt-16"
        >
          <Button
            onClick={() => navigate("/auth")}
            className="h-14 px-10 text-base font-bold rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
          >
            Start Your Healing Journey
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
